import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import CustomToolTip from '../../CommonComponents/CustomToolTip';
import { getEnvVar } from '../../../envUtils';
import { MAP_CENTER, MAP_DEFAULT_ZOOM, MAP_MIN_ZOOM, OFFLINE_MAP_CONFIG } from '../../../utils/constants';
import { offline, showToastMessage, uuidv4 } from '../../../utils/commonFunctions';
import { ActiveToolType, FeatureConfigType, GeoFeatureDataType } from '../utils/types';
import useGeoFeatures from '../hooks/useGeoFeatures';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { mapboxDrawStyles } from '../utils/mapboxDrawStyles';
import 'mapbox-gl/dist/mapbox-gl.css';
import { CrossSvg, GeoFeatureInfoSvg, GeoFenceIcon, InputSvg, TargetTrackIcon, TickSvg } from '../Svgs';
import { getAoiName, getGeometry, getUpdatedFileData, getUploadedFileData, handleFileChange, setupMapHoverEffects } from '../utils/utilities';
import { isArray, isEmpty } from 'lodash';
import MissionService from '../../../service/MissionService';
import { useParams } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import './Visualization.scss'

// @ts-ignore
// eslint-disable-next-line import/no-webpack-loader-syntax
mapboxgl.workerClass = require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default;



const GRID_SPACING_DEGREES = 10;

const Visualization = () => {
    const { mission_id } = useParams();
    const { geoFeaturesDetails, selectedFeature, setSelectedFeature, getGeoFeatures, setSidebarOpen, actionFeature } = useGeoFeatures()
    const allFeatures = useMemo(() => geoFeaturesDetails?.allFeatures || [], [geoFeaturesDetails?.allFeatures])
    const activeTool = useMemo(() => selectedFeature?.activeTool || null, [selectedFeature?.activeTool])
    const loading = useMemo(() => selectedFeature?.loading, [selectedFeature?.loading])
    const data = useMemo(() => selectedFeature?.data as FeatureConfigType, [selectedFeature?.data]);
    const mode = useMemo(() => selectedFeature?.mode, [selectedFeature]);
    const mapRef = useRef<mapboxgl.Map | null>(null);
    const drawControlRef = useRef<MapboxDraw | null>(null);
    const [mapLoaded, setMapLoaded] = useState(false); //check that map is loaded

    const activeDeleteButton = useMemo(() => (!isEmpty(data?.polygon) || !isEmpty(data?.coordinates)), [data])

    const gridData = useMemo(() => {
        const features: any[] = [];

        for (let lon = -180; lon <= 180; lon += GRID_SPACING_DEGREES) {
            features.push({
                type: 'Feature',
                geometry: {
                    type: 'LineString',
                    coordinates: [
                        [lon, -90],
                        [lon, 90],
                    ],
                },
                properties: {
                    label: `${lon}°`,
                },
            });
        }

        for (let lat = -90; lat <= 90; lat += GRID_SPACING_DEGREES) {
            features.push({
                type: 'Feature',
                geometry: {
                    type: 'LineString',
                    coordinates: [
                        [-180, lat],
                        [180, lat],
                    ],
                },
                properties: {
                    label: `${lat}°`,
                },
            });
        }

        return {
            type: 'FeatureCollection',
            features,
        };
    }, []);

    const previewAllFeatures = (drawControl) => {
        if (selectedFeature) return
        drawControl.deleteAll();
        allFeatures.forEach((feature: GeoFeatureDataType) => {
            const data = {
                id: feature?.areaOfInterestId,
                type: "Feature",
                geometry: getGeometry(feature),
                properties: {},
            };
            drawControl?.add(data);
        });
    }

    const resetStateData = () => {
        if (!drawControlRef.current) return;
        previewAllFeatures(drawControlRef.current)
        setSelectedFeature(null)
        setSidebarOpen(true)
    }

    const clearStateData = useCallback((toolType: ActiveToolType) => {
        setSelectedFeature({
            mode: 'Create',
            openModal: false,
            loading: false,
            data: {
                name: getAoiName(toolType, allFeatures),
                description: '',
                type: toolType,
            } as FeatureConfigType,
            activeTool: toolType === 'Delete' ? null : toolType
        })
        setSidebarOpen(false)
    }, [allFeatures])

    const drawControls = {
        startDrawing: useCallback((type: 'Point' | 'Polygon') => {
            if (drawControlRef.current) {
                drawControlRef.current.deleteAll();
                drawControlRef.current.changeMode(type === 'Point' ? 'draw_point' : 'draw_polygon');
            }
            clearStateData(type);
        }, [clearStateData]),
        updateFeature: useCallback((data: any) => {
            if (!drawControlRef.current) return;
            drawControlRef.current.deleteAll();
            let feature: any = null;
            feature = {
                id: uuidv4(),
                type: "Feature",
                geometry: getGeometry(data),
                properties: {},
            };
            if (feature) {
                try {
                    drawControlRef.current.add(feature);
                    setSelectedFeature((prev) => (prev ? {
                        ...prev!,
                        activeTool: (prev?.mode === 'Update' || prev?.activeTool === 'File') ? prev?.activeTool : 'Delete',
                        resetConfig: resetStateData
                    } : null))
                } catch (error) {
                    showToastMessage('error', 'Something went wrong while plotting feature');
                }
            }
        }, [getGeometry, resetStateData, showToastMessage]),
        clearAll: useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            if (drawControlRef.current) {
                drawControlRef.current.deleteAll();
            }
        }, []),
    };

    const addCustomInput = () => {
        setSidebarOpen(false)
        if (activeTool === 'Custom_Input') {
            setSelectedFeature({
                ...selectedFeature!,
                openModal: 'configuration',
            })
        } else {
            if (drawControlRef.current) {
                drawControlRef.current.deleteAll();
            }
            setSelectedFeature({
                mode: 'Create',
                loading: false,
                activeTool: 'Custom_Input',
                openModal: 'configuration',
                data: {
                    name: '',
                    description: '',
                    type: null,
                } as FeatureConfigType,
            })
        }
    }

    const updateCoordinates = (drawControl) => {
        if (!drawControl) return;
        const drawnFeatures = drawControl.getAll();
        if (drawnFeatures && drawnFeatures?.features?.length > 0) {
            const extractedData = drawnFeatures.features.map((feature) => ({
                id: feature.id,
                type: feature.geometry.type,
                coordinates: feature.geometry.coordinates,
            }));
            const details = extractedData?.[0]
            setSidebarOpen(false)
            setSelectedFeature((prev) => (prev ? {
                ...prev,
                data: details.type === "Polygon" ? {
                    ...prev?.data!,
                    type: 'Geofence',
                    polygon: details.coordinates[0],
                } : {
                    ...prev?.data!,
                    type: 'Target Track',
                    coordinates: {
                        lat: details.coordinates[1],
                        long: details.coordinates[0],
                        altitude: 0,
                        minElevationAngle: 0,
                    },
                },
                openModal: prev?.activeTool === 'File' ? false : 'details',
                resetConfig: resetStateData
            } : null));
        }
    };

    const handleUpload = async () => {
        const fileData = await handleFileChange();
        if (fileData) {
            const result = getUploadedFileData(fileData.data);
            const features = isArray(fileData?.data)
                ? fileData?.data.flatMap((item: any) => item.features || [])
                : fileData?.data?.features;
            if (!features) return
            if (!drawControlRef.current) return;
            drawControlRef.current.deleteAll();
            features.forEach((feature: any) => {
                drawControlRef.current?.add(feature);
            });
            setSidebarOpen(false)
            setSelectedFeature({
                ...selectedFeature!,
                openModal: false,
                activeTool: 'File',
                data: {
                    name: fileData.name,
                    description: '',
                    type: 'Target Track',
                    file: result || [],
                } as FeatureConfigType
            })
        }
    };

    const submitFeature = async () => {
        if (activeTool === 'File') {
            // Get current state from drawControl to capture any edits/moves made before confirmation
            let updatedSelectedFeature = selectedFeature;
            if (drawControlRef.current) {
                const currentFeatures = drawControlRef.current.getAll();
                if (currentFeatures && currentFeatures.features && currentFeatures.features.length > 0) {
                    // Convert current drawControl features back to the format expected by getUploadedFileData
                    const updatedFileData = getUploadedFileData({
                        type: 'FeatureCollection',
                        features: currentFeatures.features
                    });

                    // Create updated selectedFeature with the current edited state
                    // When activeTool is 'File', data is guaranteed to be FeatureConfigType
                    const currentData = updatedSelectedFeature?.data as FeatureConfigType;
                    updatedSelectedFeature = updatedSelectedFeature ? {
                        ...updatedSelectedFeature,
                        data: {
                            ...currentData,
                            file: updatedFileData || currentData?.file || []
                        } as FeatureConfigType
                    } : null;
                }
            }

            const formData = getUpdatedFileData(updatedSelectedFeature, allFeatures);
            const updatedMode = mode === 'Create' ? 'created' : 'updated';
            const successMessage = activeTool === 'File' ? 'File uploaded successfully' : `Feature ${updatedMode} successfully`;
            const errorMessage = activeTool === 'File' ? 'File upload failed' : `Feature ${updatedMode} failed`;
            if (!formData) return
            setSelectedFeature({
                ...updatedSelectedFeature!,
                loading: true
            })
            try {
                const response = await MissionService.postFeature(mission_id, formData);
                await getGeoFeatures();
                resetStateData();
                showToastMessage('success', successMessage);
            } catch (error: any) {
                const message = error?.response?.data?.message || errorMessage
                showToastMessage('error', message);
                setSelectedFeature({
                    ...updatedSelectedFeature!,
                    loading: false
                })
            }
        }
    }

    useEffect(() => {
        let map: any;
        if (offline()) {
            (window as any).mapboxgl = (window as any).mapboxgl || {};
            (window as any).mapboxgl.accessToken = 'not-needed';
            map = new (window as any).mapboxgl.Map({
                container: 'mapContainer',
                center: [0, 0],
                zoom: 2.3,
                minZoom: 2.3,
                maxZoom: 2.9,
                projection: 'equirectangular',
                ...OFFLINE_MAP_CONFIG,
            });
        } else {
            mapboxgl.accessToken = getEnvVar('REACT_APP_MAPBOX_TOKEN');
            map = new mapboxgl.Map({
                container: 'mapContainer',
                style: getEnvVar('REACT_APP_MAPBOX_STYLE_URL'),
                center: MAP_CENTER,
                zoom: MAP_DEFAULT_ZOOM,
                minZoom: MAP_MIN_ZOOM,
            });
        }
        map.doubleClickZoom.disable()

        const drawControl = new MapboxDraw({
            displayControlsDefault: false,
            controls: {},
            modes: { ...MapboxDraw.modes },
            styles: mapboxDrawStyles,
        });
        const handleDrawUpdate = () => {
            updateCoordinates(drawControl);
        };

        map.on('load', () => {
            map.addControl(drawControl, 'top-right');
            mapRef.current = map;
            drawControlRef.current = drawControl;
            previewAllFeatures(drawControl)
            setupMapHoverEffects(map);
            map.on('draw.create', handleDrawUpdate);
            map.on('draw.update', handleDrawUpdate);
            setMapLoaded(true);
        });
        map.on('dblclick', () => {
            const drawnFeatures = drawControl.getAll();
            if (drawnFeatures?.features?.length === 0) {
                resetStateData();
            }
        });
        map.on('draw.selectionchange', (e: any) => {
            if (e.features.length > 0) {
                setSidebarOpen(false)
            }
        });
        return () => {
            map.remove();
            mapRef.current = null;
            drawControlRef.current = null;
            map.off('draw.create', handleDrawUpdate);
            map.off('draw.update', handleDrawUpdate);
        };
    }, []);

    useEffect(() => {
        if (!mapRef.current || !drawControlRef.current) return;
        const map = mapRef.current;
        map.on('click', (e: any) => {
            const pointFeatures = map.queryRenderedFeatures(e.point);
            if (pointFeatures.length > 0) {
                const clicked = pointFeatures[0]?.properties;
                const feature = allFeatures.find((f) => f.areaOfInterestId === clicked.id);
                if (!feature) return
                actionFeature('Update', feature);
            }
        });

        previewAllFeatures(drawControlRef.current)
    }, [allFeatures, selectedFeature, drawControlRef.current, mapRef.current])

    useEffect(() => {
        if (mapLoaded && mapRef.current && (activeTool === 'Custom_Input' || mode === 'Update') && (data?.coordinates || data?.polygon)) {
            drawControls.updateFeature(data)
        }
    }, [activeTool, data, mode, mapLoaded])

    useEffect(() => {
        if (!mapLoaded || !mapRef.current) return;
        const mapInstance = mapRef.current;

        if (mapInstance?.getLayer('grid-line-layer')) {
            mapInstance.removeLayer('grid-line-layer');
        }
        if (mapInstance?.getLayer('grid-label-layer')) {
            mapInstance.removeLayer('grid-label-layer');
        }
        if (mapInstance?.getSource('grid-lines')) {
            mapInstance.removeSource('grid-lines');
        }

        mapInstance?.addSource('grid-lines', {
            type: 'geojson',
            data: gridData,
        });

        mapInstance?.addLayer({
            id: 'grid-line-layer',
            type: 'line',
            source: 'grid-lines',
            paint: {
                'line-color': '#6b7280',
                'line-width': 0.6,
                'line-opacity': 0.30,
            },
        });

        mapInstance?.addLayer({
            id: 'grid-label-layer',
            type: 'symbol',
            source: 'grid-lines',
            layout: {
                'symbol-placement': 'line',
                'text-field': ['get', 'label'],
                'text-font': ['Open Sans Regular'],
                'text-size': 8,
                'text-rotation-alignment': 'map',
                'text-pitch-alignment': 'map',
                'text-allow-overlap': false,
                'text-padding': 1,
            },
            paint: {
                'text-color': '#BEC2BE'
            },
        });

        return () => {
            const cleanupMap = mapRef.current;
            if (!cleanupMap) return;
            if (cleanupMap.getLayer('grid-label-layer')) {
                cleanupMap.removeLayer('grid-label-layer');
            }
            if (cleanupMap.getLayer('grid-line-layer')) {
                cleanupMap.removeLayer('grid-line-layer');
            }
            if (cleanupMap.getSource('grid-lines')) {
                cleanupMap.removeSource('grid-lines');
            }
        };
    }, [gridData, mapLoaded])


    return (
        <div id='map_container' className='mapbox__modal__container'>
            <div id='mapContainer' className='h-100 w-100' />
            {activeTool === 'File' &&
                <div className='mapbox__modal__header__container large'>
                    <div className='mapbox__modal__header__title__container'>
                        <i className='fe fe-file-text' />
                        <div className='d-flex align-items-center'>
                            <span className='mapbox__modal__header__title'>{selectedFeature?.data?.name}</span>
                            ({data?.file?.length})
                        </div>
                    </div>
                    <div className='d-flex align-items-center gap-2'>
                        <div className='mapbox__modal__header__buttons__container'>
                            <CustomToolTip
                                title='Save'
                                placement='bottom'
                            >
                                <button
                                    disabled={loading}
                                    className={`mapbox__header__button`}
                                    onClick={submitFeature}
                                >
                                    {loading ? <CircularProgress color='inherit' size={20} /> : <TickSvg />}
                                </button>
                            </CustomToolTip>
                        </div>
                        <div className='mapbox__modal__header__buttons__container'>
                            <CustomToolTip
                                title='Cancel'
                                placement='bottom'
                            >
                                <button
                                    disabled={loading}
                                    className={`mapbox__header__button`}
                                    onClick={resetStateData}
                                >
                                    <CrossSvg />
                                </button>
                            </CustomToolTip>
                        </div>
                    </div>
                </div>
            }
            {activeTool !== 'File' &&
                <div className='mapbox__modal__header__container'>
                    <div className='mapbox__modal__header__buttons__container'>
                        <CustomToolTip title='Geofence' placement='bottom'>
                            <button
                                disabled={activeDeleteButton || mode === 'Update'}
                                className={`mapbox__header__button ${activeTool === 'Polygon' ? 'active' : ''}`}
                                onClick={() => drawControls.startDrawing('Polygon')}
                            >
                                <GeoFenceIcon />
                            </button>
                        </CustomToolTip>
                        <CustomToolTip title='Target Track' placement='bottom'>
                            <button
                                disabled={activeDeleteButton || mode === 'Update'}
                                className={`mapbox__header__button ${activeTool === 'Point' && 'active'}`}
                                onClick={() => drawControls.startDrawing('Point')}
                            >
                                <TargetTrackIcon />
                            </button>
                        </CustomToolTip>
                        {(activeDeleteButton && mode === 'Create') &&
                            <CustomToolTip title='Remove' placement='bottom'>
                                <button
                                    className={`mapbox__header__button`}
                                    onClick={resetStateData}
                                >
                                    <i className='fe fe-trash' />
                                </button>
                            </CustomToolTip>
                        }
                        <CustomToolTip title='GeoFeature Input' placement='bottom'>
                            <button
                                disabled={activeDeleteButton || mode === 'Update'}
                                className={`mapbox__header__button ${activeTool === 'Custom_Input' && 'active'}`}
                                onClick={addCustomInput}
                            >
                                <InputSvg />
                            </button>
                        </CustomToolTip>
                    </div>
                    <div className='mapbox__modal__header__buttons__container'>
                        <CustomToolTip
                            maxWidth='400px'
                            svg={<GeoFeatureInfoSvg />}
                            title='Supported file formats: Shapefile(.zip), CSV (only for target track), TopoJson, GeoJson, KML. The file size should not be more than 1MB.'
                            placement='bottom'
                        >
                            <button
                                className={`mapbox__header__button`}
                                onClick={handleUpload}
                            >
                                <img src={require('../../../assets/images/svgs/features/upload-icon.svg').default} height={20} width={20} alt='upload' />
                            </button>
                        </CustomToolTip>
                    </div>
                </div>
            }
        </div>
    );
}

export default Visualization